import React, { useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import { fetchPowerBISemanticData } from '../services/powerbiService';
import { useUser } from '../context/UserContext';

const ResolutionMatrices = ({ userClaims }) => {
  const { user } = useUser();
  const datasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
  const workspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
  const daxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
  const tableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [sectionFilter, setSectionFilter] = useState('All');

  const baseQuery = useMemo(() => String(daxQuery || '').trim(), [daxQuery]);
  const baseTableName = useMemo(() => String(tableName || '').trim(), [tableName]);
  const safeTableName = useMemo(() => baseTableName.replace(/'/g, "''"), [baseTableName]);
  const accessLevel = String(user?.accessLevel || '').trim().toLowerCase();
  const isGlobalScope = accessLevel === 'global' || accessLevel === 'admin';

  const allowedCountryValues = useMemo(() => {
    if (isGlobalScope) return [];

    const countries = Array.isArray(user?.allowedCountries) ? user.allowedCountries : [];
    const fallbackCountry = user?.country ? [user.country] : [];

    return [...countries, ...fallbackCountry]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .filter((value) => value.toLowerCase() !== 'global' && value.toLowerCase() !== 'all')
      .filter((value, idx, arr) => arr.findIndex((v) => v.toLowerCase() === value.toLowerCase()) === idx);
  }, [isGlobalScope, user?.allowedCountries, user?.country]);

  const effectiveQuery = useMemo(() => {
    if (!safeTableName) return baseQuery || undefined;
    if (isGlobalScope || allowedCountryValues.length === 0) {
      return baseQuery || undefined;
    }

    const countryLiterals = allowedCountryValues
      .map((country) => `"${country.replace(/"/g, '""')}"`)
      .join(', ');

    return `EVALUATE FILTER('${safeTableName}', '${safeTableName}'[Country] IN {${countryLiterals}})`;
  }, [safeTableName, baseQuery, isGlobalScope, allowedCountryValues]);

  const effectiveTableName = useMemo(
    () => (effectiveQuery ? undefined : (baseTableName || undefined)),
    [effectiveQuery, baseTableName]
  );

  const normalizeColumn = (name) => {
    const raw = String(name || '').trim();
    const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
    return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
  };

  const statusColumnKey = useMemo(
    () => columns.find((col) => normalizeColumn(col) === 'implementation status') || null,
    [columns]
  );
  const sectionColumnKey = useMemo(
    () => columns.find((col) => normalizeColumn(col) === 'lead coe team (accountable)') || null,
    [columns]
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchPowerBISemanticData(
          datasetId,
          workspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );
        if (!mounted) return;
        setRows(Array.isArray(data.data) ? data.data : []);
        setColumns(Array.isArray(data.columns) ? data.columns : []);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || 'Failed to load semantic model data');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [datasetId, workspaceId, effectiveQuery, effectiveTableName]);

  const statusOptions = useMemo(() => {
    if (!statusColumnKey) return [];
    const unique = new Set(
      rows
        .map((row) => String(row[statusColumnKey] || '').trim())
        .filter(Boolean)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [rows, statusColumnKey]);

  const sectionOptions = useMemo(() => {
    if (!sectionColumnKey) return [];
    const unique = new Set(
      rows
        .map((row) => String(row[sectionColumnKey] || '').trim())
        .filter(Boolean)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [rows, sectionColumnKey]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const statusMatch =
        statusFilter === 'All' || !statusColumnKey || String(row[statusColumnKey] || '').trim() === statusFilter;
      const sectionMatch =
        sectionFilter === 'All' || !sectionColumnKey || String(row[sectionColumnKey] || '').trim() === sectionFilter;
      return statusMatch && sectionMatch;
    });
  }, [rows, statusFilter, statusColumnKey, sectionFilter, sectionColumnKey]);

  const previewRows = useMemo(() => filteredRows, [filteredRows]);

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="resolution-matrices" userClaims={userClaims} />

      <div className="p-4">
        <div className="bg-white shadow-md rounded-lg p-4">
          <h2 className="text-xl font-bold text-gray-800 mb-4">COE TA Tracker</h2>

          {loading && <div className="text-gray-600">Loading semantic model data...</div>}
          {!loading && error && <div className="text-red-600">{error}</div>}

          {!loading && !error && (
            <>
              <div className="mb-2 text-sm font-medium text-gray-700">Filter by status</div>
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStatusFilter('All')}
                  className={`px-3 py-1 rounded-full text-sm border ${
                    statusFilter === 'All'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                {statusOptions.map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      statusFilter === status
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
              <div className="mb-2 text-sm font-medium text-gray-700">Filter by section</div>
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSectionFilter('All')}
                  className={`px-3 py-1 rounded-full text-sm border ${
                    sectionFilter === 'All'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                {sectionOptions.map((section) => (
                  <button
                    key={section}
                    type="button"
                    onClick={() => setSectionFilter(section)}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      sectionFilter === section
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {section}
                  </button>
                ))}
              </div>
              <div className="overflow-auto border border-gray-200 rounded">
                <table className="min-w-full table-fixed text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {columns.map((col) => (
                        <th key={col} className="px-3 py-2 text-left font-semibold text-gray-700 break-words whitespace-normal align-top">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        {columns.map((col) => (
                          <td key={`${idx}-${col}`} className="px-3 py-2 text-gray-700 break-words whitespace-normal align-top">
                            {row[col] == null ? '' : String(row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredRows.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-500 border-t border-gray-100">
                    No requests found for selected filters.
                  </div>
                )}
              </div>
              {rows.length !== filteredRows.length && (
                <div className="mt-2 text-xs text-gray-500">
                  Filtered to {filteredRows.length} of {rows.length} rows.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResolutionMatrices;